import { ErrorHandler, LOCALE_ID, NgModule, inject, provideAppInitializer } from '@angular/core'
import { BrowserModule } from '@angular/platform-browser'
import { AppRoutingModule } from './app-routing.module'
import { AppComponent } from './app.component'
import { StoreModule } from '@ngrx/store'
import { EffectsModule } from '@ngrx/effects'
import { StoreRouterConnectingModule } from '@ngrx/router-store'
import { CurrentUserModule } from './shared/services/current-user'
import { TranslateModule } from './shared/services/translate'
import { environment } from '@env/environment'
import { AppStateReducers } from './app.state'
import { ACLModule } from './shared/services/acl'
import { SidebarMenuModule } from './shared/components/sidebar-menu'
import { AppInitializerService } from './shared/services/app-initializer/app-initializer.service'
import { JWTTokenModule } from './shared/services/jwt-token'
import { httpInterceptorProviders } from './shared/http-interceptors'
import { CurrentOrganizationModule } from './shared/services/current-organization'
import { LocalesService } from './shared/services/locales'
import { ErrorPageModule } from '@shared/pages/error-page'
import { ToasterModule } from './shared/services/toaster'
import { CurrencyPipe, DatePipe, DecimalPipe, registerLocaleData } from '@angular/common'
import localeRu from '@angular/common/locales/ru'
import { CurrentProjectModule } from './shared/services/current-project'
import { NavigateModule } from '@shared/services/navigate'
import { RegistrationFooterModule } from './shared/components/registration-layout/components/registration-footer'
import { WebsocketModule } from './shared/services/websocket'
import { TopBannerModule } from './shared/services/top-banner'
import { SidebarMobileButtonModule } from './shared/components/sidebar-menu/components/sidebar-mobile-button'
import { BreakpointsModule } from './shared/services/breakpoints'
import { ThemeServiceModule } from './shared/services/theme'
import { RegistrationSimpleHeaderModule } from './shared/components/registration-simple-layout/components/registration-simple-header'
import { ModuleErrorHandler } from './shared/services/error-sender/module-error-handler'
import { TrackedHttpClient } from './shared/services/error-sender/tracked-http-client'
import { HttpClient } from '@angular/common/http'
import { SupportWidgetModule } from './support-module/components/support-widget'

registerLocaleData(localeRu)

export function initAppFactory(appInitializerService: AppInitializerService) {
  return () => {
    return appInitializerService.init()
  }
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    ThemeServiceModule,
    CurrentUserModule,
    AppRoutingModule,
    TranslateModule,
    BreakpointsModule,
    CurrentOrganizationModule,
    NavigateModule,
    RegistrationFooterModule,
    RegistrationSimpleHeaderModule,
    JWTTokenModule,
    SidebarMenuModule,
    SidebarMobileButtonModule,
    CurrentProjectModule,
    ErrorPageModule,
    ToasterModule,
    WebsocketModule,
    TopBannerModule,
    ACLModule,
    SupportWidgetModule,
    StoreModule.forRoot(AppStateReducers),
    EffectsModule.forRoot(),
    StoreRouterConnectingModule.forRoot(),
    ...environment.imports,
  ],
  providers: [
    httpInterceptorProviders,
    provideAppInitializer(() => {
      const initializerFn = initAppFactory(inject(AppInitializerService))
      return initializerFn()
    }),
    {
      provide: LOCALE_ID,
      deps: [LocalesService],
      useFactory: (LocalesService) => LocalesService.get(),
    },
    { provide: ErrorHandler, useClass: ModuleErrorHandler },
    { provide: HttpClient, useClass: TrackedHttpClient },
    CurrencyPipe,
    DecimalPipe,
    DatePipe,
  ],

  bootstrap: [AppComponent],
})
export class AppModule {}
